import { getAppBaseUrl } from '../../_utilities/utilities'
import { getInquiryFieldDefinition } from '../../_configuration/inquiry-fields'
import {
  AnonClaimPopupMessage,
  AnonContactDetails,
} from '../../_types/anon-session'
import { InquiryFieldDefinition } from '../../_types/inquiry-field'
import { InquireFormConfig } from './types'

const ALWAYS_REQUIRED_FIELD_KEYS = ['name', 'email']

const ANON_CLAIM_POPUP_PATH = '/#/anon-claim'

const ANON_CLAIM_POPUP_FEATURES = 'width=460,height=620,menubar=no,toolbar=no'

const ANON_CLAIM_TIMEOUT_MS = 90000

/**
 * True when this document is running inside the cross-site SDK overlay rather
 * than the standalone app. The SDK mounts the app in an iframe, so a parent
 * window that is not ourselves means we are embedded.
 *
 * @returns {boolean}
 */
const isEmbedded = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  return window.parent !== window
}

/**
 * Asks the host SDK to close the overlay. No-op when not embedded. Mirrors the
 * `{ source: 'quickpass', type: 'close' }` message the SDK listens for.
 *
 * @returns {void}
 */
const requestOverlayClose = (): void => {
  if (isEmbedded() === false) {
    return
  }

  window.parent.postMessage({ source: 'quickpass', type: 'close' }, '*')
}

/**
 * Opens the first-party `/anon-claim` popup, hands it the renter's contact
 * details over a same-origin postMessage channel, and resolves with the
 * cross-site session token it mints. The popup is what establishes the
 * first-party storage state (cookie + Storage Access eligibility) that lets
 * every future host site recognise this renter.
 *
 * Must be called from within a user-gesture handler (the submit click) or the
 * browser will block the popup.
 *
 * @param {AnonContactDetails} contact - the name/email/phone the renter typed
 * @returns {Promise<string>} the minted cross-site session token
 */
const runAnonClaimPopup = (contact: AnonContactDetails): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const expectedOrigin = window.location.origin
    const popup = window.open(
      `${getAppBaseUrl()}${ANON_CLAIM_POPUP_PATH}`,
      'quickpass-anon-claim',
      ANON_CLAIM_POPUP_FEATURES,
    )

    if (popup === null) {
      reject(
        new Error(
          'We could not open the QuickPass window — please allow popups and try again.',
        ),
      )
      return
    }

    let settled = false

    const cleanup = (): void => {
      window.removeEventListener('message', handleMessage)
      window.clearTimeout(timeoutHandle)

      if (popup.closed === false) {
        popup.close()
      }
    }

    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== expectedOrigin) {
        return
      }

      const data = event.data as Partial<AnonClaimPopupMessage>

      if (data === null || data.source !== 'quickpass-anon-claim') {
        return
      }

      if (data.type === 'ready') {
        popup.postMessage(
          {
            source: 'quickpass-anon-claim-host',
            type: 'contact',
            payload: contact,
          },
          expectedOrigin,
        )
        return
      }

      if (data.type === 'done' && typeof data.token === 'string') {
        settled = true
        cleanup()
        resolve(data.token)
        return
      }

      if (data.type === 'error') {
        settled = true
        cleanup()
        reject(new Error(data.message ?? 'Could not set up your QuickPass.'))
      }
    }

    const timeoutHandle = window.setTimeout(() => {
      if (settled === false) {
        cleanup()
        reject(new Error('Setting up your QuickPass timed out.'))
      }
    }, ANON_CLAIM_TIMEOUT_MS)

    window.addEventListener('message', handleMessage)
  })
}

const splitFieldKeys = (raw: string | null): string[] => {
  if (raw === null) {
    return []
  }

  return raw
    .split(',')
    .map(value => value.trim())
    .filter(value => value !== '')
}

/**
 * Resolves the host site's `?fields=` and `?required=` query params into the
 * concrete set of curated fields to render and the keys that are mandatory.
 * Only catalogue-known field keys survive (the allowlist guarantee). A field
 * named in `required` but omitted from `fields` is still shown — required
 * implies visible. Name + email are always required.
 *
 * @param {URLSearchParams} search - the inquiry route's query params
 * @returns {InquireFormConfig}
 */
const parseInquiryFormConfig = (search: URLSearchParams): InquireFormConfig => {
  const fieldKeys = splitFieldKeys(search.get('fields'))
  const requestedRequiredKeys = splitFieldKeys(search.get('required'))

  const curatedFields: InquiryFieldDefinition[] = []
  const seenKeys = new Set<string>()

  for (const key of [...fieldKeys, ...requestedRequiredKeys]) {
    if (seenKeys.has(key) === true) {
      continue
    }

    const definition = getInquiryFieldDefinition(key)

    if (definition === null) {
      continue
    }

    seenKeys.add(key)
    curatedFields.push(definition)
  }

  const requiredKeys = Array.from(
    new Set([...ALWAYS_REQUIRED_FIELD_KEYS, ...requestedRequiredKeys]),
  )

  return { curatedFields, requiredKeys }
}

/**
 * Reduces the form's working field values down to a sanitised custom-fields
 * map — only catalogue fields that are present in the form, with trimmed
 * non-empty values. The server re-validates against its own catalogue.
 *
 * @param {InquiryFieldDefinition[]} curatedFields - the rendered curated fields
 * @param {Record<string, string>} fieldValues - the current form values keyed by field key
 * @returns {Record<string, string>}
 */
const collectCustomFields = (
  curatedFields: InquiryFieldDefinition[],
  fieldValues: Record<string, string>,
): Record<string, string> => {
  const result: Record<string, string> = {}

  for (const field of curatedFields) {
    const value = (fieldValues[field.key] ?? '').trim()

    if (value !== '') {
      result[field.key] = value
    }
  }

  return result
}

export {
  isEmbedded,
  requestOverlayClose,
  runAnonClaimPopup,
  parseInquiryFormConfig,
  collectCustomFields,
}
